import { z } from 'zod';

/**
 * Money is ALWAYS an integer number of minor units (e.g. cents) plus an ISO 4217 currency code.
 * Never represent money as a float anywhere in this codebase - DB columns, API payloads, UI state.
 * `amountMinor` is a JS `number` (safe up to 2^53-1) rather than bigint because it must round-trip
 * through JSON (D1/HTTP/KV) without serialization helpers; ticket/invoice amounts never approach
 * that limit in practice.
 */
export const CURRENCY_CODES = ['AUD', 'USD', 'NZD', 'GBP', 'EUR', 'CAD'] as const;
export type CurrencyCode = (typeof CURRENCY_CODES)[number];

export const currencyCodeSchema = z.enum(CURRENCY_CODES);

export const moneySchema = z.object({
  amountMinor: z.number().int(),
  currency: currencyCodeSchema,
});

export type Money = z.infer<typeof moneySchema>;

export const DEFAULT_CURRENCY: CurrencyCode = 'AUD';

export class CurrencyMismatchError extends Error {
  constructor(a: CurrencyCode, b: CurrencyCode) {
    super(`Currency mismatch: ${a} vs ${b}`);
    this.name = 'CurrencyMismatchError';
  }
}

function assertSameCurrency(a: Money, b: Money): void {
  if (a.currency !== b.currency) throw new CurrencyMismatchError(a.currency, b.currency);
}

export function money(amountMinor: number, currency: CurrencyCode = DEFAULT_CURRENCY): Money {
  if (!Number.isInteger(amountMinor)) {
    throw new TypeError(`amountMinor must be an integer, got ${amountMinor}`);
  }
  return { amountMinor, currency };
}

export function zero(currency: CurrencyCode = DEFAULT_CURRENCY): Money {
  return money(0, currency);
}

export function addMoney(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return money(a.amountMinor + b.amountMinor, a.currency);
}

export function subtractMoney(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return money(a.amountMinor - b.amountMinor, a.currency);
}

export function negateMoney(a: Money): Money {
  return money(-a.amountMinor, a.currency);
}

export function sumMoney(items: Money[], currency: CurrencyCode = DEFAULT_CURRENCY): Money {
  return items.reduce((acc, m) => addMoney(acc, m), zero(currency));
}

export function isZero(a: Money): boolean {
  return a.amountMinor === 0;
}

export function isNegative(a: Money): boolean {
  return a.amountMinor < 0;
}

/** Multiply by a rate (e.g. percentage fee), rounding half-up to the nearest minor unit. */
export function multiplyMoney(a: Money, rate: number): Money {
  return money(Math.round(a.amountMinor * rate), a.currency);
}

/**
 * Split an amount into `n` nearly-equal integer parts that sum exactly back to the original
 * (distributes the remainder one minor unit at a time, largest index first).
 */
export function splitMoney(a: Money, n: number): Money[] {
  if (n <= 0) throw new RangeError('n must be > 0');
  const base = Math.floor(a.amountMinor / n);
  const remainder = a.amountMinor - base * n;
  return Array.from({ length: n }, (_, i) => money(base + (i < remainder ? 1 : 0), a.currency));
}

const MINOR_UNITS_PER_MAJOR: Record<CurrencyCode, number> = {
  AUD: 100,
  USD: 100,
  NZD: 100,
  GBP: 100,
  EUR: 100,
  CAD: 100,
};

/** Human-readable formatting for display only - never parse this back into a stored amount. */
export function formatMoney(a: Money, locale = 'en-AU'): string {
  const divisor = MINOR_UNITS_PER_MAJOR[a.currency];
  return new Intl.NumberFormat(locale, { style: 'currency', currency: a.currency }).format(
    a.amountMinor / divisor,
  );
}

/** Convert a major-unit decimal (e.g. form input "12.50") into Money. Only use at input boundaries. */
export function moneyFromMajorUnits(
  majorUnits: string | number,
  currency: CurrencyCode = DEFAULT_CURRENCY,
): Money {
  const divisor = MINOR_UNITS_PER_MAJOR[currency];
  const n = typeof majorUnits === 'string' ? Number(majorUnits) : majorUnits;
  if (!Number.isFinite(n)) throw new TypeError(`Invalid amount: ${majorUnits}`);
  return money(Math.round(n * divisor), currency);
}
